/**
 * Weight display units for the Care+ My Account pages (UK: stones/pounds or kg).
 * Weights are always stored and sent to the API in kg — only what the customer sees and types
 * switches. The unit is a per-browser preference; outside the UK it is always kg.
 */
import { useState } from "react";
import { kgToImperial, imperialToStoredKg } from "../pages/Questionnaire/components/ImperialInput/imperialUtils";

export type WeightUnit = "kg" | "st";

const STORAGE_KEY = "care_plus_weight_unit";

/** Only the UK offers stones/pounds. */
export const supportsImperialWeight = (country?: string | null): boolean => (country ?? "").toLowerCase() === "gb";

export const readWeightUnit = (country?: string | null): WeightUnit => {
  if (!supportsImperialWeight(country)) return "kg";
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "kg" || stored === "st") return stored;
  } catch {
    // storage unavailable — fall back to the UK default
  }
  return "st";
};

export const useWeightUnit = (country?: string | null): [WeightUnit, (unit: WeightUnit) => void] => {
  const [unit, setUnit] = useState<WeightUnit>(() => readWeightUnit(country));
  const update = (next: WeightUnit) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // keep the choice for this page view only
    }
    setUnit(next);
  };
  return [supportsImperialWeight(country) ? unit : "kg", update];
};

/** "11 st 3 lb"; a weight under a stone reads "9 lb" (for differences such as "lost 9 lb"). */
export const formatStLb = (kg: number): string => {
  const { st, lb } = kgToImperial(Math.abs(kg));
  return st > 0 ? `${st} st ${lb} lb` : `${lb} lb`;
};

const roundKg = (kg: number) => Math.round(kg * 10) / 10;

/** Weight with its unit: "71 kg" or "11 st 3 lb". */
export const formatWeight = (kg: number, unit: WeightUnit): string =>
  unit === "st" ? formatStLb(kg) : `${roundKg(kg)} kg`;

/**
 * Fills a translated template written for kg ("{kg} kg from your goal", "Weight (kg)") in the
 * chosen unit. In kg the placeholder gets the number, so existing copy is unchanged; in stones the
 * placeholder and the " kg" after it are replaced by the full imperial value.
 */
export const fillWeight = (template: string, placeholder: string, kg: number, unit: WeightUnit): string => {
  if (unit === "kg") return template.split(placeholder).join(String(roundKg(kg)));
  const escaped = placeholder.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return template.replace(new RegExp(`${escaped}\\s*kg`, "g"), formatStLb(kg)).split(placeholder).join(formatStLb(kg));
};

export const stLbToKg = imperialToStoredKg;

export { kgToImperial };
